import _ from "lodash";
import { doc, getDoc } from "firebase/firestore";
import FirestoreDocument from "./document";
import { db } from "../../config/firebase";
import Logger from "utils/logger";
import { MethodCategories } from "../methods";

export default class Methods extends FirestoreDocument {
  static COLLECTION = "app";
  static DOCUMENT_ID = "methods";
  static CACHE_KEY = "app_methods_catalog_v1";

  constructor() {
    super();
    this.id = Methods.DOCUMENT_ID;
    this.methodsData = {};
    this.saveCheckpoint();
  }

  /**
   * Load methods data from Firestore
   * @returns {Promise<void>}
   */
  async load() {
    // Serve the cached catalog first: it's one app-wide, basically-static doc, so
    // a cached copy lets category bars render instantly (and keep working offline).
    try {
      const cached = localStorage.getItem(Methods.CACHE_KEY);
      if (cached) {
        this.methodsData = JSON.parse(cached);
        this.saveCheckpoint();
      }
    } catch {
      // ignore a corrupt cache entry
    }

    // Fetch directly rather than via FirestoreDocument.fetch(): that path is wrapped
    // by the query monitor, which would raise the user-facing "Check your connection"
    // toast if this cosmetic background read is slow on a stale socket. The category
    // bar is not worth alarming the user over -- if the fetch fails we silently keep
    // the cached (or empty) catalog.
    try {
      const snap = await getDoc(
        doc(db, Methods.COLLECTION, Methods.DOCUMENT_ID)
      );
      if (snap.exists()) {
        this.methodsData = snap.data() || {};
        this.saveCheckpoint();
        try {
          localStorage.setItem(
            Methods.CACHE_KEY,
            JSON.stringify(this.methodsData)
          );
        } catch {
          // storage full / unavailable -- non-fatal, we just won't cache
        }
      }
    } catch (error) {
      Logger.error(`[Methods] Failed to load methods data: ${error.message}`);
      if (_.isEmpty(this.methodsData)) this.methodsData = {};
    }
    return Promise.resolve();
  }

  /**
   * Map Firestore category names to UI category names
   * @param {string} firestoreCategory - The category name from Firestore
   * @returns {string} - The UI category name
   */
  mapCategoryToUI(firestoreCategory) {
    if (!firestoreCategory) {
      return "Other";
    }

    // Create a reverse mapping from display names to keys
    const displayToKey = {};
    Object.entries(MethodCategories).forEach(([key, displayName]) => {
      displayToKey[displayName] = key;
    });

    // Handle specific mappings for common variations
    const categoryMappings = {
      "3D Printed": MethodCategories.DIY,
      DIY: MethodCategories.DIY,
      "Dual Tension": MethodCategories.DT,
      Inflation: MethodCategories.Inflation,
      Manual: MethodCategories.Manual,
      Other: MethodCategories.Other,
      Retaining: MethodCategories.Retaining,
      Taping: MethodCategories.Taping,
      Traction: MethodCategories.Traction,
      Traditional: MethodCategories.Traditional,
      Weights: MethodCategories.Weights,
    };

    // First try direct mapping
    if (categoryMappings[firestoreCategory]) {
      return categoryMappings[firestoreCategory];
    }

    // Then try reverse mapping from display names
    if (displayToKey[firestoreCategory]) {
      return displayToKey[firestoreCategory];
    }

    // Default to Other if no mapping found
    Logger.debug(
      `[Methods] No UI mapping found for category: ${firestoreCategory}`
    );
    return "Other";
  }

  /**
   * Get the category for a given method name (alias)
   * @param {string} methodName - The method name or alias to look up
   * @returns {string|null} - The category name, or null if not found
   */
  getCategoryForMethod(methodName) {
    if (!methodName || !_.isString(methodName)) {
      return null;
    }

    const trimmedMethod = _.trim(methodName);

    // Search through all categories and their methods/aliases
    for (const [categoryName, methods] of Object.entries(this.methodsData)) {
      for (const [, aliases] of Object.entries(methods)) {
        if (
          _.isArray(aliases) &&
          aliases.some((alias) => _.toLower(alias) === _.toLower(trimmedMethod))
        ) {
          // Map the Firestore category name to UI category name
          return this.mapCategoryToUI(categoryName);
        }
      }
    }

    Logger.debug(`[Methods] No category found for method: ${methodName}`);
    return null;
  }

  /**
   * Get the official method name for a given alias
   * @param {string} alias - The alias to look up
   * @returns {string|null} - The official method name, or null if not found
   */
  getOfficialMethodName(alias) {
    if (!alias || !_.isString(alias)) {
      return null;
    }

    const trimmedAlias = _.trim(alias);

    // Search through all categories and their methods/aliases
    for (const [, methods] of Object.entries(this.methodsData)) {
      for (const [officialName, aliases] of Object.entries(methods)) {
        if (
          _.isArray(aliases) &&
          aliases.some(
            (aliasItem) => _.toLower(aliasItem) === _.toLower(trimmedAlias)
          )
        ) {
          return officialName;
        }
      }
    }

    Logger.debug(`[Methods] No official method found for alias: ${alias}`);
    return null;
  }

  /**
   * Get all methods for a specific category
   * @param {string} categoryName - The category to get methods for
   * @returns {Object} - Object mapping official names to alias arrays
   */
  getMethodsForCategory(categoryName) {
    if (!categoryName || !_.isString(categoryName)) {
      return {};
    }

    return _.get(this.methodsData, categoryName, {});
  }

  /**
   * Get all available categories
   * @returns {Array<string>} - Array of category names
   */
  getAllCategories() {
    return Object.keys(this.methodsData);
  }

  /**
   * Get all methods with their categories and aliases
   * @returns {Array<Object>} - Array of method objects with category, official name, and aliases
   */
  getAllMethodsFlattened() {
    const allMethods = [];

    _.forEach(this.methodsData, (methods, categoryName) => {
      _.forEach(methods, (aliases, officialName) => {
        allMethods.push({
          category: this.mapCategoryToUI(categoryName),
          officialName,
          aliases: _.cloneDeep(aliases),
        });
      });
    });

    return allMethods;
  }

  /**
   * Search for methods by partial name match
   * @param {string} searchTerm - The term to search for
   * @returns {Array<Object>} - Array of matching method objects
   */
  searchMethods(searchTerm) {
    if (!searchTerm || !_.isString(searchTerm)) {
      return [];
    }

    const lowerSearchTerm = _.toLower(_.trim(searchTerm));
    const results = [];

    _.forEach(this.methodsData, (methods, categoryName) => {
      _.forEach(methods, (aliases, officialName) => {
        // Check if search term matches official name or any alias
        const matchesOfficial =
          _.toLower(officialName).includes(lowerSearchTerm);
        const matchingAliases = aliases.filter((alias) =>
          _.toLower(alias).includes(lowerSearchTerm)
        );

        if (matchesOfficial || matchingAliases.length > 0) {
          results.push({
            category: this.mapCategoryToUI(categoryName),
            officialName,
            aliases: _.cloneDeep(aliases),
            matchingAliases,
          });
        }
      });
    });

    return results;
  }

  /**
   * Check if methods data has been loaded
   * @returns {boolean} - True if data is loaded, false otherwise
   */
  get isLoaded() {
    return !_.isEmpty(this.methodsData);
  }

  /**
   * Get JSON representation for saving (not needed for read-only document)
   * @returns {Object}
   */
  get json() {
    return this.methodsData;
  }

  /**
   * The loaded catalog, cached for the app's lifetime. The charts call
   * Method.getCategory/getOfficialName hundreds of times (per method, per month);
   * without this each call built a fresh Methods and re-fetched the doc, turning a
   * profile chart into a 30s+ stall. One shared promise -> every lookup resolves
   * in-memory. Catalog is app-wide and ~static, so a lifetime singleton is fine.
   * @returns {Promise<Methods>} Loaded Methods instance
   */
  static _instancePromise = null;
  static getInstance() {
    if (Methods._instancePromise) return Methods._instancePromise;
    const promise = (async () => {
      const methods = new Methods();
      await methods.load();
      return methods;
    })();
    // Let a failed load retry rather than caching a rejected promise forever.
    promise.catch(() => {
      if (Methods._instancePromise === promise) Methods._instancePromise = null;
    });
    Methods._instancePromise = promise;
    return promise;
  }
}
